import { BestMatchType, MediaVariants } from "@is/constants";
import {
  ColorVariant,
  MediaType,
  MediaVariant,
  ProductItem,
  Size,
  Style,
} from "@is/types";
import { ProductPrice, ProductPriceWithRange } from "@is/types/style";
import { getEnvValue } from "@is/utils/env";
import { getImageUri, getVideoUri, isVideo } from "@is/utils/media";

export const addMediaVariants = (
  MediaMap: Map<string, MediaType>,
  media: MediaType,
  imageVariants: Array<MediaVariant> = [],
  videoVariants: Array<MediaVariant> = []
) => {
  imageVariants.forEach((variant: MediaVariant) => {
    const url = getImageUri(
      getEnvValue("VITE_IMAGE_SERVICE_URL"),
      media.url,
      variant
    );
    MediaMap.set(url, {
      ...media,
      url,
    });
  });

  if (isVideo(media)) {
    videoVariants.forEach((variant: MediaVariant) => {
      const url = getVideoUri(
        getEnvValue("VITE_VIDEO_SERVICE_URL"),
        media.url,
        variant
      );
      MediaMap.set(url, {
        ...media,
        url,
      });
    });
  }
};

export const getStylesMedia = (styles: Array<Style>): Array<MediaType> => {
  const MediaMap = new Map<string, MediaType>();

  const add = (media: MediaType) => {
    addMediaVariants(
      MediaMap,
      media,
      [MediaVariants.MEDIUM, MediaVariants.LARGE, MediaVariants.HUGE],
      [MediaVariants.LARGE]
    );
  };

  styles.forEach((style: Style) => {
    style.media?.forEach(add);

    style.variants?.forEach((colorVariant: ColorVariant) => {
      colorVariant.media.forEach(add);
    });
  });

  return Array.from(MediaMap.values());
};

export const getStylePriceRange = (
  sizeInfos: Size[]
): ProductPriceWithRange => {
  if (!sizeInfos?.some((sizeInfo) => sizeInfo.prices?.length)) {
    return undefined;
  }

  let currencyCode = "";

  const prices = sizeInfos.reduce((acc, item) => {
    if (item.prices) {
      item.prices.forEach((price: ProductPrice) => {
        currencyCode = price.currencyCode;
        if (price.salePrice) {
          acc.push(price.salePrice);
        } else {
          acc.push(price.retailPrice);
        }
      });
    }
    return acc;
  }, [] as number[]);

  const priceMax = Math.max(...prices);
  const priceMin = Math.min(...prices);

  const finalPrice =
    priceMax === priceMin ? `${priceMax}` : `${priceMin} - ${priceMax}`;

  return {
    currencyCode,
    retailPrice: finalPrice,
  };
};

export const getStyleBestMatchFlag = (style: ProductItem) => {
  const bestMatchType = style.meta.type;

  if (!style.meta) {
    return false;
  }

  const isBestMatch =
    bestMatchType === BestMatchType.Id ||
    bestMatchType === BestMatchType.Partial ||
    (bestMatchType === BestMatchType.Multi &&
      style.meta.matchedAllVariantAttributes);

  return isBestMatch;
};

export const getAllSizesAndLengths = (variants: ColorVariant[]) => {
  if (!variants?.some((variant) => variant.sizeInfos?.length)) {
    return { sizes: [], lengths: [] };
  }

  return variants.reduce(
    (acc, variant, index) => {
      variant.sizeInfos.forEach((item) => {
        if (item.size && !acc.sizes.includes(item.size)) {
          acc.sizes.push(item.size);
        }

        if (item.length && !acc.lengths.includes(item.length)) {
          acc.lengths.push(item.length);
        }
      });

      if (index === variants.length - 1) {
        const sortedLengths = acc.lengths ?? [];
        sortedLengths.sort((a, b) => a.localeCompare(b));

        return {
          sizes: acc.sizes,
          lengths: sortedLengths,
        };
      }

      return acc;
    },
    {
      sizes: [],
      lengths: [],
    }
  );
};
